import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getCpDocumentSignContext } from "../services/cpDocumentSignContext";
import { DraftCanvas } from "./CpDocumentDraftsPage";
import { normalizeCpDraftPolicies, samePdfPages } from "./cpDraftFields";
import { resolveCpDraftField } from "./cpDraftPreview";
import { cpDocumentFieldContext } from "./cpDocumentContext";
import CpSignaturePad from "../components/CpSignaturePad";
import { closeTabOrGoTo } from "./signTabClose";
import { frontendAppUrl, frontendCollabUrl } from "../config/envs";
import { previewCpDocument, signCpDocument } from "../services/cpDocumentSigning";
import { fitSignatureImage } from "../services/cpSignatureImage";

const buttonClass = "bg-[#cfc9ff] px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-purple-600";
const inputClass = "w-full border border-gray-300 rounded px-3 py-2 mt-2 disabled:bg-gray-100";
// Empty boxes on the signer's PDF stay blank (ENG-2482).
const NO_PLACEHOLDERS = {};

function SigningDocument({ data, secret, reload }) {
  const { document, signing } = data;
  const [fieldValues, setFieldValues] = useState(() => Object.fromEntries(signing.fields.filter((field) => field.editable)
    .map((field) => [field.field_key, field.value ?? (field.field_type === "checkbox" ? false : "")])));
  const [signature, setSignature] = useState("");
  const [preview, setPreview] = useState(null);
  const [reviewReady, setReviewReady] = useState(false);
  const [renderReady, setRenderReady] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [frozen, setFrozen] = useState(false);
  const [mustReload, setMustReload] = useState(false);
  const active = useRef(true);
  const operation = useRef(0);
  const pending = useRef(false);
  const capture = useRef(0);
  // A new key clears the pad, so it never shows a drawing the page did not accept.
  const [padKey, setPadKey] = useState(0);
  const submittedPayload = useRef(null);
  const currentPdf = useRef(null);
  currentPdf.current = preview?.file || document.snapshot.pdf.url;
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; operation.current += 1; submittedPayload.current = null; };
  }, []);

  const invalidate = () => {
    operation.current += 1;
    setPreview(null);
    setReviewReady(false);
    setRenderReady(false);
    setConsent(false);
    setError("");
    setPdfError("");
  };
  const updateField = (key, value) => {
    invalidate();
    setFieldValues((values) => ({ ...values, [key]: value }));
  };
  // Each stroke on the pad sends the whole drawing; a newer one replaces an older one still being read.
  const captureSignature = async (file) => {
    if (frozen || mustReload) return;
    const attempt = ++capture.current;
    if (!file) {
      invalidate();
      setSignature("");
      pending.current = false;
      setBusy("");
      return;
    }
    const refuse = (message) => { setSignature(""); setPadKey((key) => key + 1); setError(message); };
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      refuse("Use a PNG or JPG signature of 3 MB or less.");
      return;
    }
    pending.current = true;
    setBusy("signature");
    try {
      const fitted = await fitSignatureImage(file);
      if (!active.current || attempt !== capture.current) return;
      if (fitted.size > 3 * 1024 * 1024) { refuse("Use a PNG or JPG signature of 3 MB or less."); return; }
      const value = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read the signature."));
        reader.readAsDataURL(fitted);
      });
      if (!active.current || attempt !== capture.current) return;
      invalidate();
      setSignature(value);
    } catch {
      if (active.current && attempt === capture.current) refuse("Could not read your signature. Please try again.");
    } finally {
      if (active.current && attempt === capture.current) { pending.current = false; setBusy(""); }
    }
  };
  const review = async () => {
    if (pending.current || frozen || mustReload) return;
    const missing = signing.fields.filter((field) => field.editable && field.required
      && (field.field_type === "checkbox" ? fieldValues[field.field_key] !== true : !String(fieldValues[field.field_key] ?? "").trim()));
    if (missing.length || !signature) {
      setError("Complete the required fields and add your signature before continuing.");
      return;
    }
    pending.current = true;
    const attempt = ++operation.current;
    setBusy("preview");
    setError("");
    setPdfError("");
    setPreview(null);
    setConsent(false);
    setReviewReady(false);
    setRenderReady(false);
    try {
      const result = await previewCpDocument(secret, { field_values: fieldValues, signature_image: signature });
      if (active.current && attempt === operation.current) setPreview({ ...result, file: { data: result.pdf_bytes } });
    } catch (failure) {
      if (active.current && attempt === operation.current) {
        setError(failure.message);
        setMustReload([401, 403, 404, 409, 200].includes(failure.status));
      }
    } finally {
      if (active.current && attempt === operation.current) { pending.current = false; setBusy(""); }
    }
  };
  const submit = async () => {
    if (pending.current || !preview || !reviewReady || !renderReady || !consent || pdfError || mustReload) return;
    pending.current = true;
    const attempt = ++operation.current;
    submittedPayload.current = submittedPayload.current || {
      field_values: { ...fieldValues }, signature_image: signature, reviewed_sha256: preview.pdf_sha256,
      consent: true, consent_version: signing.consent.version,
    };
    setFrozen(true);
    setBusy("sign");
    setError("");
    try {
      const result = await signCpDocument(secret, submittedPayload.current);
      if (active.current && attempt === operation.current) {
        if (result.role_key !== document.role_key) {
          setError("The document returned an unexpected response. Reload to confirm whether your signature was recorded.");
          setMustReload(true);
        } else {
          setReceipt(result);
          setSignature("");
          setFieldValues({});
          setPreview(null);
          submittedPayload.current = null;
        }
      }
    } catch (failure) {
      if (active.current && attempt === operation.current) {
        setError(failure.message);
        setMustReload([401, 403, 404, 409, 200].includes(failure.status));
        if (failure.status === 400) {
          submittedPayload.current = null;
          setFrozen(false);
          setPreview(null);
          setConsent(false);
          setReviewReady(false);
          setRenderReady(false);
        }
      }
    } finally {
      if (active.current && attempt === operation.current) { pending.current = false; setBusy(""); }
    }
  };
  const backToEdit = () => {
    if (pending.current || frozen) return;
    invalidate();
  };
  const onPages = useCallback((pages, file) => {
    if (!active.current || file !== currentPdf.current) return;
    if (!samePdfPages(document.snapshot.pdf.pages, pages)) {
      setPdfError("The document pages do not match the saved version. Reload before continuing.");
      setReviewReady(false);
    } else if (preview) setReviewReady(true);
  }, [document.snapshot.pdf.pages, preview]);
  const onPdfError = useCallback((message, file) => {
    if (active.current && file === currentPdf.current) {
      setPdfError("Could not open the document PDF. Reload before continuing.");
      setReviewReady(false);
    }
  }, []);
  const onRendered = useCallback((file) => {
    if (active.current && preview && file === currentPdf.current) setRenderReady(true);
  }, [preview]);
  const signed = Boolean(receipt) || signing.state === "signed";
  const eligible = signing.state === "eligible" && !signed;
  const locked = Boolean(busy) || frozen || mustReload;
  const sourceFields = document.snapshot.fields.filter((field) => field.signer_role === document.role_key);
  // Record values already print on the PDF preview; list only what the signer can change, in the order it sits on the PDF.
  const placed = Object.fromEntries(sourceFields.map((field) => [field.field_key, field]));
  const position = (field) => [placed[field.field_key]?.page_number ?? 0, placed[field.field_key]?.position_y ?? 0, placed[field.field_key]?.position_x ?? 0];
  const listed = signing.fields.filter((field) => field.field_type !== "signature" && field.editable)
    .sort((a, b) => position(a).reduce((order, value, index) => order || value - position(b)[index], 0));
  // The address prints on several lines; edit it as it prints, as sign-all does.
  const multiline = new Set(sourceFields.filter((field) => field.field_type === "text" && field.source_key === "address").map((field) => field.field_key));
  const sampleValues = Object.fromEntries(signing.fields.map((field) => [field.field_key, {
    status: field.status, editable: field.editable,
    value: field.field_type === "checkbox" ? (field.editable ? fieldValues[field.field_key] : field.value) === true ? "☑" : "☐"
      : field.field_type === "signature" ? signature ? "Signature added" : "" : field.editable ? fieldValues[field.field_key] : field.value,
  }]));
  const step = preview ? 2 : 1;
  // The Documents page the signer came from: the portal for the physician, the dashboard for the clinician.
  const documentsUrl = document.role_key === "physician" ? frontendCollabUrl && `${frontendCollabUrl}/documents`
    : frontendAppUrl && `${frontendAppUrl}/app/operator/documents`;
  const outcome = receipt?.document_status;
  return <main className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-sentry-element="CPDocumentSigning">
    <header className="flex flex-wrap items-end justify-between gap-4">
      <h1 className="text-2xl font-semibold">{document.name || "Document"}</h1>
      {eligible && <ol className="flex flex-wrap gap-2 text-sm" aria-label="Signing steps">{[[1, "Fill in & sign"], [2, "Confirm"]].map(([number, label]) => <li key={number} aria-current={step === number ? "step" : undefined}
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${step === number ? "bg-gray-900 border-gray-900 text-white font-semibold" : "border-gray-400 text-gray-700"}`}>
        <span aria-hidden="true" className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ${step === number ? "bg-white text-gray-900" : "border border-gray-400"}`}>{step > number ? "✓" : number}</span>{label}</li>)}</ol>}
    </header>
    {/* A signer sees only their own part: never who else signs, in what order, or why a document is on hold (ENG-2482). */}
    {signed ? <section className={`border rounded p-5 space-y-2 ${outcome === "void" ? "bg-amber-50 text-amber-900" : "bg-green-50 text-green-900"}`} role="status">
      <h2 className="font-semibold">{outcome === "void" ? "This document is no longer available" : "Your signature is recorded"}</h2>
      <p>{outcome === "void" ? "No new signature was added." : "You don't need to do anything else."}</p>
      <p className="text-sm">Contact your administrator if you need a signed copy.</p>
      {documentsUrl && <button type="button" className={`${buttonClass} mt-2 text-gray-900`} onClick={() => closeTabOrGoTo(window, documentsUrl)}>Back to documents</button>}
    </section>
      : !eligible ? <section className="border rounded p-5 space-y-3 bg-gray-50" role="status">
        <p>This document is not available to sign yet. Please check back later.</p>
        <button type="button" className={buttonClass} disabled={Boolean(busy)} onClick={reload}>Reload document</button>
      </section>
      : <>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(16rem,0.8fr)] gap-5">
          <section className="min-w-0" aria-label="Document PDF"><h2 className="font-semibold mb-2">{preview ? "This is exactly what you sign" : "Document preview"}</h2>{pdfError ? <p role="alert" className="text-red-700">{pdfError}</p>
            : <DraftCanvas key={preview ? `review-${preview.pdf_sha256}` : "draft"} file={preview?.file || document.snapshot.pdf.url} pages={document.snapshot.pdf.pages} fields={preview ? [] : sourceFields} selectedKey={null} onPages={onPages} onError={onPdfError} onRendered={preview ? onRendered : undefined} zoom={1} readOnly sampleValues={preview ? null : sampleValues} placeholders={NO_PLACEHOLDERS} ariaLabel={preview ? "Completed document for review" : "Document preview"} />}</section>
          <section className="min-w-0 bg-white border rounded p-4 space-y-4" aria-label={step === 2 ? "Confirm and sign" : "Your document fields"}>
            {step === 1 ? <>
              <h2 className="font-semibold">Before you sign</h2>
              {listed.length > 0 && <ul className="divide-y text-sm">{listed.map((field) => <li key={field.field_key} className="py-3 break-words">
              <label htmlFor={`cp-field-${field.field_key}`} className="font-medium">{field.label}{!field.required && <span className="text-xs font-normal text-gray-500"> · Optional</span>}</label>
              {field.field_type === "checkbox" ? <input id={`cp-field-${field.field_key}`} type="checkbox" className="block mt-2" checked={fieldValues[field.field_key] === true} onChange={(event) => updateField(field.field_key, event.target.checked)} disabled={locked} />
                : multiline.has(field.field_key) ? <textarea id={`cp-field-${field.field_key}`} rows={3} className={inputClass} value={fieldValues[field.field_key] ?? ""} maxLength={2000} onChange={(event) => updateField(field.field_key, event.target.value)} disabled={locked} />
                : <input id={`cp-field-${field.field_key}`} type={field.field_type === "date" ? "date" : "text"} className={inputClass} value={fieldValues[field.field_key] ?? ""} maxLength={2000} onChange={(event) => updateField(field.field_key, event.target.value)} disabled={locked} />}
            </li>)}</ul>}
              <div className={listed.length > 0 ? "border-t pt-4 space-y-2" : "space-y-2"}><h3 className="font-medium">Your signature</h3>
                <CpSignaturePad key={padKey} value={signature} onChange={captureSignature} disabled={frozen || mustReload || busy === "preview" || busy === "sign"} /></div>
              <button type="button" className={`${buttonClass} w-full`} disabled={locked || Boolean(pdfError)} onClick={review}>{busy === "preview" ? "Preparing your document…" : "Continue to confirm"}</button>
              <p className="text-xs text-gray-500">Nothing is signed until you confirm on the next step.</p>
            </> : <section className="space-y-3" aria-label="Sign reviewed document">
              <h2 className="font-semibold">Sign this document</h2>
              <p className="text-xs text-gray-600">{signing.consent.text}</p>
              <label className="flex gap-2 items-start text-sm"><input type="checkbox" checked={consent} disabled={Boolean(busy) || !reviewReady || !renderReady || Boolean(pdfError) || mustReload} onChange={(event) => setConsent(event.target.checked)} /><span>I reviewed the completed document and agree to use my electronic signature.</span></label>
              <button type="button" className={`${buttonClass} w-full`} disabled={Boolean(busy) || !reviewReady || !renderReady || !consent || Boolean(pdfError) || mustReload} onClick={submit}>{busy === "sign" ? "Recording signature…" : frozen ? "Retry Adopt and Sign" : "Adopt and Sign"}</button>
              <button type="button" className="text-sm underline text-purple-700 disabled:opacity-50" disabled={Boolean(busy) || frozen} onClick={backToEdit}>Back to edit</button>
              <p className="text-xs text-gray-500">Signing date: {preview.signing_date} · Signing does not change your license activation.</p>
            </section>}
            {/* Beside the button the signer just used: the step 1 panel can run past the screen. */}
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            {(error || pdfError) && <button type="button" className={buttonClass} disabled={Boolean(busy)} onClick={reload}>Reload document</button>}
          </section>
        </div>
      </>}
  </main>;
}

export default function CpDocumentSignPage() {
  const { signSecretId } = useParams();
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState("");
  const [pdfError, setPdfError] = useState("");
  const [retry, setRetry] = useState(0);
  const activeView = useRef({ secret: signSecretId, retry });
  activeView.current = { secret: signSecretId, retry };
  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setError("");
    setPdfError("");
    getCpDocumentSignContext(signSecretId).then((data) => {
      if (!cancelled) setLoaded({ secret: signSecretId, retry, data });
    }).catch((failure) => { if (!cancelled) setError(failure.message); });
    return () => { cancelled = true; };
  }, [signSecretId, retry]);

  const data = loaded?.secret === signSecretId && loaded.retry === retry ? loaded.data : null;
  const onPages = useCallback((pages) => {
    if (data && loaded.secret === activeView.current.secret && loaded.retry === activeView.current.retry
      && !samePdfPages(data.document.snapshot.pdf.pages, pages)) setPdfError("The document pages do not match the saved version. Please reload; if this continues, contact your administrator.");
  }, [data, loaded]);
  const onPdfError = useCallback(() => {
    if (loaded?.secret === activeView.current.secret && loaded.retry === activeView.current.retry) setPdfError("Could not open the document PDF. Please reload; if this continues, contact your administrator.");
  }, [loaded]);
  const reloadButton = <button type="button" className="bg-[#cfc9ff] px-4 py-2 rounded" onClick={() => setRetry((count) => count + 1)}>Reload document</button>;
  if (error) return <div className="max-w-3xl mx-auto p-6 space-y-4"><p role="alert">{error}</p>{reloadButton}</div>;
  if (!data) return <p role="status" className="p-6">Loading document…</p>;
  if (data.signing) return <SigningDocument key={`${signSecretId}-${retry}`} data={data} secret={signSecretId} reload={() => setRetry((count) => count + 1)} />;

  const document = data.document;
  const snapshot = normalizeCpDraftPolicies(document.snapshot, data.record_sources);
  const fields = snapshot.fields.filter((field) => field?.signer_role === document.role_key);
  const context = cpDocumentFieldContext(data);
  const values = Object.fromEntries(fields.map((field) => [field.field_key, resolveCpDraftField(field, context, data.record_sources)]));
  return <main className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-sentry-element="CPDocumentReadOnly">
    <header><h1 className="text-2xl font-semibold">{document.name || "Document"}</h1></header>
    <p role="status" className="border rounded bg-gray-50 p-4">This document is not available to sign yet. Please check back later.</p>
    <div>
      <section className="min-w-0" aria-label="Document PDF">{pdfError ? <div className="space-y-3"><p role="alert" className="text-red-700">{pdfError}</p>{reloadButton}</div>
        : <DraftCanvas file={snapshot.pdf.url} pages={snapshot.pdf.pages} fields={fields} selectedKey={null} onPages={onPages} onError={onPdfError} zoom={0.75} readOnly sampleValues={values} placeholders={NO_PLACEHOLDERS} ariaLabel="Document preview" />}</section>
      {!pdfError && <div className="mt-4">{reloadButton}</div>}
    </div>
  </main>;
}
